/**
 * Composed line transforms at fixed chart times, reused across frames (W3-02a, UX-025).
 *
 * Hit-effect sprites are placed with the line transform at their EMISSION time (R§5.2), so every frame of a burst's
 * life (hitFxDuration, about 30 frames) asked `ctx.lineState(line, emissionTime)` again, composing the whole father
 * chain each time. The engine graph keeps those transforms here, keyed by (line, time) and valid while the compiled
 * graph, the aspect ratio and the muted layers stay the same; anything else clears the cache. The values are copies
 * of what `ctx.lineState` returns, so the cache changes nothing but the cost.
 *
 * `lineTransformOf(ctx, line, t)` uses the cache when the context provides one (the engine graph's context) and
 * falls back to `ctx.lineState` (test contexts, verify mode).
 */

import type { LineId } from '../model/types';
import type { Sec } from '../time/types';
import type { Mat2D } from '../util/types';
import type { EngineCompileContext, LineStateRt } from './types';

/** The transform part of a line state. */
export interface LineTransform {
  /** Translate(x, y) · Rotate(rot): local -> world. */
  readonly m: Mat2D;
  /** Composed rotation (world degrees CCW). */
  readonly rot: number;
  /** Own rotation (world degrees CCW). */
  readonly ownRot: number;
}

/** A compile context that can answer cached transforms. */
export interface LineTransformSource {
  lineTransform(line: LineId, t: Sec): LineTransform;
}

function hasTransformCache(ctx: EngineCompileContext): ctx is EngineCompileContext & LineTransformSource {
  return typeof (ctx as Partial<LineTransformSource>).lineTransform === 'function';
}

/** The line transform at t: cached when the context supports it, else `ctx.lineState(line, t)`. */
export function lineTransformOf(ctx: EngineCompileContext, line: LineId, t: Sec): LineTransform {
  return hasTransformCache(ctx) ? ctx.lineTransform(line, t) : ctx.lineState(line, t);
}

/** Entries kept before the cache starts over (one entry per (line, time) pair; a few frames of bursts). */
export const TRANSFORM_CACHE_LIMIT = 4096;

/** Cache of transforms by line index and time, valid for one (graph stamp, aspect, muted layers) key. */
export class TransformCache {
  private stamp = Number.NaN;
  private ar = Number.NaN;
  private muted: unknown = undefined;
  private readonly byLine = new Map<number, Map<number, LineTransform>>();
  private count = 0;

  get size(): number {
    return this.count;
  }

  clear(): void {
    this.byLine.clear();
    this.count = 0;
  }

  /**
   * The transform of the line at export index `index` at t; `compute` evaluates the state on a miss (its result is
   * copied, so a pooled state may be returned).
   */
  get(index: number, t: Sec, stamp: number, ar: number, muted: unknown, compute: () => LineStateRt): LineTransform {
    if (stamp !== this.stamp || !Object.is(ar, this.ar) || muted !== this.muted) {
      this.clear();
      this.stamp = stamp;
      this.ar = ar;
      this.muted = muted;
    }
    let line = this.byLine.get(index);
    const hit = line?.get(t);
    if (hit !== undefined) return hit;
    const s = compute();
    const entry: LineTransform = { m: [s.m[0], s.m[1], s.m[2], s.m[3], s.m[4], s.m[5]], rot: s.rot, ownRot: s.ownRot };
    if (this.count >= TRANSFORM_CACHE_LIMIT) {
      this.clear();
      line = undefined;
    }
    if (line === undefined) {
      line = new Map();
      this.byLine.set(index, line);
    }
    line.set(t, entry);
    this.count++;
    return entry;
  }
}
